import posthog from 'posthog-js';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { ValuesType } from 'utility-types';
import { CompletedAnim } from '~/modules/Elements/Menu/Heading';
import InputManager from '~/modules/GameEngine/Input/InputManager';
import useViewportSize from '~/modules/hooks/useViewportSize';
import startViewTransition from '~/modules/utils/startViewTransition';
import storage from '~/modules/utils/storage';

import SelectPreference from '~/routes/SelectInput/SelectPreference';
import Advanced from '~/routes/SelectInput/Variants/Advanced';
import BuiltIn from '~/routes/SelectInput/Variants/BuiltIn';
import DifferentMics from '~/routes/SelectInput/Variants/DifferentMics';
import MultipleMics from '~/routes/SelectInput/Variants/MultipleMics';
import RemoteMics from '~/routes/SelectInput/Variants/RemoteMics';
import SingStarMics from '~/routes/SelectInput/Variants/SingStarMics';
import Skip from '~/routes/SelectInput/Variants/Skip';
import { MicSetupPreference, MicSetupPreferenceSetting, useSettingValue } from '~/routes/Settings/SettingsState';

interface Props {
  onFinish?: (pref: ValuesType<typeof MicSetupPreference>) => void;
  onBack?: () => void;
  smooth?: boolean;
  closeButtonText: string;
  skipText?: string;
  playerNames?: string[];
  context?: 'page' | 'preview';
  hideFooter?: boolean;
  renderFooter?: (args: { onBack?: () => void; onFinish?: () => void; isComplete: boolean }) => ReactNode;
}

const LAST_SELECTED_KEY = 'Previously selected input type';
const previouslySelected = storage.local.getItem(LAST_SELECTED_KEY);

type PreferenceValue = ValuesType<typeof MicSetupPreference>;
type SelectablePreference = PreferenceValue | 'multiple-mics' | null;

function isFinishablePreference(value: SelectablePreference): value is PreferenceValue {
  return value !== null && value !== 'multiple-mics';
}

function SelectInputView({
  onFinish,
  closeButtonText,
  onBack,
  skipText,
  smooth = true,
  context = 'page',
  hideFooter,
  renderFooter,
}: Props) {
  const { width } = useViewportSize();
  const isMobile = (width ?? 9999) < 640;
  const isPreview = context === 'preview';

  const shouldHideFooter = hideFooter ?? isPreview;

  const [storedPreference, setStoredPreference] = useSettingValue(MicSetupPreferenceSetting);
  const [preference, setPreference] = useState<SelectablePreference>(
    storedPreference === 'skip' ? null : storedPreference,
  );
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    const wasMonitoring = InputManager.monitoringStarted();
    InputManager.startMonitoring();
    return () => {
      if (!wasMonitoring) {
        InputManager.stopMonitoring();
      }
    };
  }, []);

  const storePreference = (pref: PreferenceValue | 'multiple-mics') => {
    if (smooth && pref !== 'skip') {
      startViewTransition(() => {
        flushSync(() => setPreference(pref));
      });
    } else {
      setPreference(pref);
    }
  };

  const onSave = (pref: PreferenceValue) => () => {
    setStoredPreference(pref === 'skip' ? (storedPreference ?? 'skip') : pref);
    storage.local.setItem(LAST_SELECTED_KEY, pref);
    posthog.capture('sourcePreferenceSet', { source: pref });
    onFinish?.(pref);
  };

  const back = () => {
    startViewTransition(() => {
      setPreference(null);
      setIsComplete(false);
    });
  };

  const handleFooterFinish = () => {
    if (!onFinish || !isFinishablePreference(preference)) return;
    onFinish(preference);
  };

  const headerText = useMemo(() => {
    if (isComplete) return 'Setup completed!';
    return 'How do you want to sing?';
  }, [isComplete]);

  const footer = renderFooter?.({
    onBack,
    onFinish: isFinishablePreference(preference) ? handleFooterFinish : undefined,
    isComplete,
  });

  return (
    <div
      className={['w-full', 'flex flex-col', isPreview ? 'h-[100dvh] overflow-hidden' : 'min-h-0'].join(' ')}
      style={
        isPreview ? { background: 'var(--walky-canvas)', color: 'var(--walky-ink)' } : { color: 'var(--walky-ink)' }
      }>
      <div
        className={[
          'shrink-0',
          isPreview ? 'border-t-4 border-b px-4 pt-4 pb-4 backdrop-blur-md sm:px-6 sm:pt-6' : 'mb-4 px-2 sm:px-0',
        ].join(' ')}
        style={
          isPreview
            ? {
                background: 'var(--walky-surface-translucent)',
                borderColor: 'var(--walky-border)',
                borderTopColor: 'var(--walky-orange)',
              }
            : undefined
        }>
        <div
          className="text-active text-center font-semibold tracking-wide"
          style={{
            fontSize: isMobile
              ? isPreview
                ? 'clamp(22px, 7vw, 30px)'
                : 'clamp(24px, 8vw, 40px)'
              : isPreview
                ? 'clamp(34px, 4vw, 56px)'
                : 'clamp(34px, 4vw, 52px)',
            lineHeight: 1.05,
          }}>
          {isComplete ? (
            <>
              <CompletedAnim /> {headerText}
            </>
          ) : (
            headerText
          )}
        </div>
      </div>

      <div
        className={[
          'min-h-0 flex-1',
          isPreview ? 'overflow-y-auto overscroll-contain px-3 py-3 sm:px-6 sm:py-5' : 'overflow-visible px-2 sm:px-0',
        ].join(' ')}>
        <div className={['mx-auto w-full', isPreview ? 'max-w-[1220px]' : 'max-w-[980px]'].join(' ')}>
          <div
            className={[
              isPreview
                ? 'rounded-lg border p-3 backdrop-blur-xl sm:p-5'
                : 'rounded-none border-0 bg-transparent p-0 shadow-none',
            ].join(' ')}
            style={
              isPreview
                ? {
                    background: 'var(--walky-surface)',
                    borderColor: 'var(--walky-border)',
                    boxShadow: 'var(--walky-shadow-strong)',
                  }
                : undefined
            }>
            {(preference === null || preference === 'skip') && (
              <SelectPreference
                onPreferenceSelected={storePreference}
                previouslySelected={previouslySelected}
                onBack={onBack}
                skipText={skipText}
                context={context}
              />
            )}

            {preference === 'remoteMics' && (
              <RemoteMics
                onSetupComplete={setIsComplete}
                onBack={back}
                onSave={onSave('remoteMics')}
                closeButtonText={closeButtonText}
                changePreference={setPreference}
              />
            )}

            {preference === 'multiple-mics' && (
              <MultipleMics
                onSetupComplete={setIsComplete}
                onBack={back}
                closeButtonText={closeButtonText}
                changePreference={setPreference}
              />
            )}

            {preference === 'singstar-mics' && (
              <SingStarMics
                onSetupComplete={setIsComplete}
                onBack={back}
                onSave={onSave('singstar-mics')}
                closeButtonText={closeButtonText}
                changePreference={setPreference}
              />
            )}

            {preference === 'different-mics' && (
              <DifferentMics
                onSetupComplete={setIsComplete}
                onBack={back}
                onSave={onSave('singstar-mics')}
                closeButtonText={closeButtonText}
                changePreference={setPreference}
              />
            )}

            {preference === 'built-in' && (
              <BuiltIn
                onSetupComplete={setIsComplete}
                onBack={back}
                onSave={onSave('built-in')}
                closeButtonText={closeButtonText}
                changePreference={setPreference}
              />
            )}

            {preference === 'advanced' && (
              <Advanced
                onSave={onSave('advanced')}
                onSetupComplete={setIsComplete}
                onBack={back}
                closeButtonText={closeButtonText}
                changePreference={setPreference}
              />
            )}

            {preference === 'skip' && (
              <Skip
                onSetupComplete={setIsComplete}
                onBack={back}
                onSave={onSave('skip')}
                closeButtonText={closeButtonText}
                changePreference={setPreference}
              />
            )}
          </div>
        </div>
      </div>

      {!shouldHideFooter && footer ? <div className="mt-3">{footer}</div> : null}
    </div>
  );
}

export default SelectInputView;
